import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { UntilDestroy } from '@ngneat/until-destroy';
import { distinctUntilChanged } from 'rxjs';

import { PuSubscribable } from '@app/utils/pu-subscribe';
import { ImageCaptureMode } from '@main-application/inspections/models/rest-inspections-model.interface';
import {
  AreaPageElementCommentModel,
  AreaPageElementFileModel,
} from '@main-application/shared/template-editor-dialog/models/template-editor-model';
import { EColorPalette } from '@shared/enums/color-palette.enum';
import { EIcon } from '@shared/enums/icon.enum';
import { IRadioButtonOption } from '@shared/interfaces/radio-button-option.interface';

import { TemplateEditorDialogItemComponent } from '../template-editor-item.component';

export const fileOptions: IRadioButtonOption<ImageCaptureMode>[] = [
  { value: ImageCaptureMode.PhotoAndGallery, label: 'Photos (camera & gallery)' },
  { value: ImageCaptureMode.PhotoOnly, label: 'Photos (camera only)' },
  { value: ImageCaptureMode.AnyFile, label: 'Photos & Documents' },
];

@UntilDestroy()
@Component({
  selector: 'app-template-editor-constant-questions-item',
  templateUrl: './template-editor-constant-questions.component.html',
  styleUrls: ['../question/template-editor-question.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TemplateEditorDialogConstantQuestionsComponent extends PuSubscribable implements OnInit {
  EIcon = EIcon;
  EColorPalette = EColorPalette;
  ImageCaptureMode = ImageCaptureMode;

  @Input() file: AreaPageElementFileModel;
  @Input() comment: AreaPageElementCommentModel;

  @Output() remove = new EventEmitter<AreaPageElementFileModel | AreaPageElementCommentModel>();
  @Output() questionItemChange = new EventEmitter<AreaPageElementFileModel | AreaPageElementCommentModel>();

  readonly fileOptions = fileOptions;
  fileSectionExpanded = false;
  selectedFileOptionLabel: string;

  constructor(
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private templateEditorDialogItemComponent: TemplateEditorDialogItemComponent
  ) {
    super();
  }

  ngOnInit(): void {
    const option = this.fileOptions.find(o => o.value === this.file.imageCaptureMode) ?? this.fileOptions[0];
    this.selectedFileOptionLabel = option ? option.label : 'files';

    this.templateEditorDialogItemComponent.expandQuestion$
      .pipe(distinctUntilChanged())
      .subscribe(expandedId => {
        if (expandedId !== 'file') {
          this.fileSectionExpanded = false;
          this.cdr.markForCheck();
        }
      })
      .untilDestroyed(this);
  }

  updateFileOption(value: ImageCaptureMode) {
    const option = this.fileOptions.find(o => o.value === value);
    this.selectedFileOptionLabel = option ? option.label : 'files';
    this.questionItemChange.emit({ ...this.file, imageCaptureMode: value });
  }

  toggleSetting(id: 'file') {
    if (id === 'file') {
      this.fileSectionExpanded = !this.fileSectionExpanded;
      if (this.fileSectionExpanded) {
        this.templateEditorDialogItemComponent.expandQuestion$.next(id);
      }
    }
  }

  expandSetting(id: 'file'): void {
    if (id === 'file') {
      if (!this.fileSectionExpanded) {
        this.fileSectionExpanded = true;

        if (this.file?.visible === false) {
          this.file.visible = true;
          this.remove.emit({ ...this.file, visible: this.file.visible });
        }

        this.templateEditorDialogItemComponent.expandQuestion$.next(id);
        this.cdr.markForCheck();
      }
    }
  }

  toggleRemove(id: 'file' | 'comment') {
    if (id === 'file') {
      const current = this.file?.visible === false ? false : true;
      this.file.visible = !current;
      if (!this.file.visible) this.fileSectionExpanded = false;
      this.remove.emit({ ...this.file, visible: this.file.visible });
    }

    if (id === 'comment') {
      const current = this.comment?.visible === false ? false : true;
      this.comment.visible = !current;
      this.remove.emit({ ...this.comment, visible: this.comment.visible });
    }
  }
}
